import React from 'react';
import { getPreviewKeyFromFileInfo } from 'js/file_viewer/core/data/preview_key';
import { previewMetadataPrefetch } from 'js/file_viewer/core/queries/preview_metadata';
import { prefetchArchiveRivieraData } from 'js/file_viewer/core/queries/riviera_preview_types/archive';
import { prefetchAudioVideoRivieraData } from 'js/file_viewer/core/queries/riviera_preview_types/audio_video';
import { prefetchLinkfileRivieraData } from 'js/file_viewer/core/queries/riviera_preview_types/linkfile';
import { prefetchSsrDocRivieraData } from 'js/file_viewer/core/queries/riviera_preview_types/ssr_doc';
import { prefetchTextRivieraData } from 'js/file_viewer/core/queries/riviera_preview_types/text';
import { ImageLogger, rivieraClient } from 'js/file_viewer/core/riviera/api';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { getApproximateImageSizeScale } from 'js/file_viewer/core/utils/sizing_utils';
import { resolvePreviewType } from 'js/file_viewer/core/utils/support';
const preloadIframe = async (srcUrl) => {
    if (!srcUrl) {
        return;
    }
    const preloadLink = document.createElement('link');
    preloadLink.href = srcUrl;
    preloadLink.rel = 'preload';
    preloadLink.as = 'fetch';
    document.head.appendChild(preloadLink);
};
const prefetchPreviewData = (queryClient, previewKey, previewMetadata, preloadVideojs) => {
    const previewType = resolvePreviewType(previewMetadata);
    switch (previewType) {
        case PreviewType.Video:
        case PreviewType.Audio:
            return prefetchAudioVideoRivieraData(queryClient, previewKey, previewMetadata, preloadVideojs);
        case PreviewType.Linkfile:
            return prefetchLinkfileRivieraData(queryClient, previewKey, previewMetadata);
        case PreviewType.SsrDoc:
        case PreviewType.CloudDoc:
            return prefetchSsrDocRivieraData(queryClient, previewKey, previewMetadata);
        case PreviewType.Archive:
            return prefetchArchiveRivieraData(queryClient, previewKey, previewMetadata);
        case PreviewType.Text:
            return prefetchTextRivieraData(queryClient, previewKey, previewMetadata);
        case PreviewType.Excel:
        case PreviewType.RawHTML:
            return preloadIframe(previewMetadata.preview_url);
        default:
            return Promise.resolve();
    }
};
/**
 * Preloads preview data in an array of files. Preload the previous two files and the next six, if available.
 * @param previewData An array of files, some of which will be preloaded.
 * @param currentIndex The index in `previewData` of the file being previewed.
 * @param viewport The viewport's size. If provided, preloaded images will be scaled to the viewport's size.
 * @note Currently, only previews of type Image are preloaded.
 * @note The file at `currentIndex` is NOT preloaded. Call maybePrefetchFile to prefetch a specific file.
 */
export function maybePrefetchCollection(previewData, currentIndex, logger, isMobile) {
    if (previewData.length === 0) {
        return;
    }
    const indexSet = [];
    [1, 2, 3, 4, 5, 6, -1, -2].forEach(function (i) {
        const ind = (currentIndex + i + previewData.length) % previewData.length;
        if (indexSet.indexOf(ind) === -1) {
            indexSet.push(ind);
        }
    });
    indexSet.forEach(function (indexToPreload) {
        if (previewData.length > indexToPreload && indexToPreload > 0) {
            const metadata = previewData[indexToPreload];
            maybePrefetchFile(metadata, logger, isMobile);
        }
    });
}
export const FileViewerPrefetch = (metadata, queryClient, fileInfo, preloadVideojs, isMobile, logger) => {
    const promises = [maybePrefetchFile(metadata, logger, isMobile)];
    if (metadata && fileInfo) {
        const previewKey = getPreviewKeyFromFileInfo(fileInfo);
        promises.push(prefetchPreviewData(queryClient, previewKey, metadata, preloadVideojs));
    }
    return Promise.all(promises);
};
/**
 * Prefetch useful data to help us reach TTVC faster.
 * This hook may be fired multiple times during the File Viewer lifecycle so it is critical to ensure that any
 * functionality added here is idempotent and inexpensive.
 */
export const useFileViewerPrefetch = (queryClient, metadata, fileInfo, apiV2ClientShim, prefetchVideojs, isMobile, sharedLinkInfo, logger) => {
    React.useMemo(() => {
        // We only want to prefetch this if previewMetadata is not passed into File Viewer.
        if (metadata === undefined) {
            previewMetadataPrefetch([fileInfo], apiV2ClientShim, queryClient, sharedLinkInfo).then((data) => {
                FileViewerPrefetch(data === null || data === void 0 ? void 0 : data.apiData.results[0].preview, queryClient, fileInfo, prefetchVideojs, isMobile, logger);
            });
        }
        else {
            FileViewerPrefetch(metadata, queryClient, fileInfo, prefetchVideojs, isMobile, logger);
        }
        // intentionally not including logger, no reason to prefetch again if the logger updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileInfo, metadata, queryClient]);
};
const maybePrefetchFile = (metadata, logger, isMobile) => {
    var _a;
    switch ((_a = metadata === null || metadata === void 0 ? void 0 : metadata.content) === null || _a === void 0 ? void 0 : _a['.tag']) {
        case 'image': {
            return prefetchImage(metadata, logger, isMobile);
        }
        case 'ssr_doc': {
            return prefetchDoc(metadata.content.image_url_tmpl);
        }
        default:
            return Promise.resolve();
    }
};
/**
 * Preloads an image from Riviera.
 */
const prefetchImage = (metadata, logger, isMobile) => {
    var _a;
    const content = ((_a = metadata.content) === null || _a === void 0 ? void 0 : _a['.tag']) === 'image' ? metadata.content : undefined;
    if (!(content === null || content === void 0 ? void 0 : content.default_src)) {
        return Promise.resolve();
    }
    // Mobile web always uses the full size image.
    if (metadata.dimensions && !isMobile) {
        const sizeScale = getApproximateImageSizeScale(metadata.dimensions);
        return rivieraClient.fetchImage(content.default_src, content.src_set, sizeScale + 'vw', logger ? ImageLogger('optimized', logger) : undefined);
    }
    if (!(content === null || content === void 0 ? void 0 : content.full_size_src)) {
        return rivieraClient.fetchImage(content.default_src, undefined, undefined, logger ? ImageLogger('default', logger) : undefined);
    }
    return Promise.all([
        rivieraClient.fetchImage(content.full_size_src, undefined, undefined, logger ? ImageLogger('full-size', logger) : undefined),
        rivieraClient.fetchImage(content.default_src, undefined, undefined, logger ? ImageLogger('default', logger) : undefined),
    ]);
};
const prefetchDoc = (imageUrlTmpl) => {
    return rivieraClient.fetchDocImage({ imageUrlTmpl });
};
//# sourceMappingURL=frontend_prefetch.preenvesnextjs.map
